import { Box, Flex, Icon, IconButton, Heading, Text, Divider, Label, List, ListItem } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import _ from 'lodash';
import { type PortalSettings } from '@gorilla/portal-shared/src/schemas';
import { Duplicate, AddSmall, Apps, Column, Person, Inbox } from '@vibe/icons';
import { type Board } from '@gorilla/common/src/lib/monday-api/api';
import { ExternalLink } from './ExternalLink';
import React from 'react';

type ClientPlaceholder = {
  placeholder: string;
  type: 'client';
  description: string;
};

type AccountPlaceholder = {
  placeholder: string;
  type: 'account';
  description: string;
};

type PlaceholdersModalProps = {
  clientBoard: Board;
  portalSettings: PortalSettings;
  onClose: () => void;
  isInsertMode?: boolean;
  onInsertPlaceholder?: (value: string) => void;
};

export function PlaceholdersModal({ clientBoard, portalSettings, onClose, isInsertMode, onInsertPlaceholder }: PlaceholdersModalProps) {
  const placeholders: (ClientPlaceholder | AccountPlaceholder)[] = [];

  placeholders.push({
    placeholder: '{{client.id}}',
    type: 'client',
    description: 'Client ID',
  });

  clientBoard.columns.forEach((column) => {
    if (column.type === 'subtasks') {
      return;
    }

    placeholders.push({
      placeholder: `{{client.${column.id}}}`,
      type: 'client',
      description: column.title,
    });
  });

  placeholders.push({
    placeholder: '{{account.firstname}}',
    type: 'account',
    description: 'First Name',
  });
  placeholders.push({
    placeholder: '{{account.lastname}}',
    type: 'account',
    description: 'Last Name',
  });
  placeholders.push({
    placeholder: '{{account.email}}',
    type: 'account',
    description: 'Email',
  });

  return (
    <AppModal
      show={true}
      onClose={() => {
        onClose();
      }}
      contentSpacing={false}
      alertDialog={true}
      width={550}
      id="placeholders__modal"
    >
      <AppModalContent>
        <Box>
          <Flex direction="row" align="center" gap={8}>
            <Icon icon={Apps} size={24} style={{ transform: 'translate3d(0,-1px,0)' }} />
            <Heading type="h3" weight="bold" align="start">
              Placeholders
            </Heading>
          </Flex>

          <Box
            rounded="small"
            border={true}
            borderColor="uiBorderColor"
            style={{
              margin: '24px 0 8px 0',
            }}
          >
            <Box
              style={{
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 300px)',
              }}
            >
              {placeholders.length === 0 ? (
                <Box
                  style={{
                    padding: '32px 16px',
                    backgroundColor: 'rgba(103, 104, 121, 0.025)',
                  }}
                >
                  <Flex direction="column" gap={4}>
                    <Icon icon={Inbox} size={24} />
                    <Text type="text2" align="center" color="primary" element="p">
                      No placeholders
                    </Text>
                  </Flex>
                </Box>
              ) : (
                <List
                  style={{
                    overflow: 'hidden',
                  }}
                >
                  {placeholders.map((entry, index) => (
                    <React.Fragment key={index}>
                      <ListItem size="medium" className="portalmanager__content__listitem--noclick portalmanager__content__listitem">
                        <Flex style={{ width: '100%' }}>
                          <Box style={{ flex: 1 }}>
                            <Flex style={{ width: '100%' }}>
                              <Flex gap={8} style={{ width: 'inherit' }}>
                                <Text
                                  type="text2"
                                  align="start"
                                  color="primary"
                                  element="p"
                                  style={{
                                    flex: entry.type === 'account' ? 0 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <Icon icon={entry.type === 'account' ? Person : Column} size={18} useCurrentColor />
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      flex: 1,
                                      display: 'inline-block',
                                      marginRight: entry.type === 'account' ? 0 : '16px',
                                      marginLeft: '8px',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {entry.description}
                                  </span>
                                </Text>

                                {entry.type === 'account' ? (
                                  <Label text="Login method" size="small" kind="line" color="dark" className="custom_small_label" />
                                ) : null}
                              </Flex>
                            </Flex>
                          </Box>

                          <Box style={{ marginLeft: 'auto' }}>
                            <Flex gap={16}>
                              <Text type="text2" align="start" color="secondary" element="p">
                                {entry.placeholder}
                              </Text>
                              <IconButton
                                tooltipContent={isInsertMode && onInsertPlaceholder ? 'Insert placeholder' : 'Copy placeholder'}
                                tooltipProps={{ position: 'left', showOnDialogEnter: false }}
                                icon={isInsertMode && onInsertPlaceholder ? AddSmall : Duplicate}
                                size={isInsertMode && onInsertPlaceholder ? 'xs' : 'small'}
                                kind={isInsertMode && onInsertPlaceholder ? 'primary' : 'tertiary'}
                                onClick={() => {
                                  if (isInsertMode && onInsertPlaceholder) {
                                    onInsertPlaceholder(entry.placeholder);
                                  } else {
                                    navigator.clipboard.writeText(`${entry.placeholder}`);
                                  }
                                }}
                                className="custom__deletepagebutton"
                              />
                            </Flex>
                          </Box>
                        </Flex>
                      </ListItem>
                      {index !== placeholders.length - 1 && <Divider withoutMargin />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          <Text type="text2" align="start" color="secondary" element="p" style={{ marginTop: '24px', marginBottom: '8px' }}>
            Placeholders display client specific column values from the Clients Board.{' '}
            <ExternalLink href="https://getgorilla.app/products/client-portal/docs/features/builder/placeholders" target="_blank">
              Learn more
            </ExternalLink>
          </Text>
        </Box>
      </AppModalContent>
    </AppModal>
  );
}
